import React from 'react';
import { PlayIcon, HomeIcon, SearchIcon, LibraryIcon, LayoutGridIcon, ChevronRightIcon } from 'lucide-react';
interface ApplePodcastsPreviewProps {
  videos: any[];
  onRemoveVideo: (id: string) => void;
  onUpdateVideo: (id: string, updates: any) => void;
}
export const ApplePodcastsPreview = ({
  videos,
  onRemoveVideo,
  onUpdateVideo
}: ApplePodcastsPreviewProps) => {
  // Only display custom videos with thumbnails
  const customVideos = videos.filter(video => video.isCustom && video.customThumbnail);
  // If no custom videos, show placeholder
  if (customVideos.length === 0) {
    return <div className="flex flex-col items-center justify-center p-8 text-center" data-id="element-11">
        <div className="bg-gray-800 rounded-full p-4 mb-4" data-id="element-12">
          <PlayIcon size={32} className="text-purple-500" data-id="element-13" />
        </div>
        <h3 className="text-xl font-medium mb-2" data-id="element-14">No thumbnails yet</h3>
        <p className="text-gray-400 mb-4" data-id="element-15">
          Upload thumbnails to see how they'll appear in Apple Podcasts
        </p>
      </div>;
  }
  // Get up to 3 videos for different sections
  const mainVideo = customVideos[0];
  const secondVideo = customVideos.length > 1 ? customVideos[1] : mainVideo;
  const recommendedVideos = customVideos.slice(0, 3);
  // Use podcast cover image if available, otherwise use the thumbnail
  const mainCoverImage = mainVideo.podcastCoverImage || mainVideo.customThumbnail;
  const secondCoverImage = secondVideo.podcastCoverImage || secondVideo.customThumbnail;
  return <div className="flex justify-center" data-id="element-16">
      <div className="w-full max-w-sm" data-id="element-17">
        <div className="relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-800 border-[14px] rounded-[2.5rem] h-[600px] w-[300px] shadow-xl" data-id="element-18">
          <div className="w-[148px] h-[18px] bg-gray-800 top-0 rounded-b-[1rem] left-1/2 -translate-x-1/2 absolute" data-id="element-19"></div>
          <div className="h-[46px] w-[3px] bg-gray-800 absolute -left-[17px] top-[124px] rounded-l-lg" data-id="element-20"></div>
          <div className="h-[46px] w-[3px] bg-gray-800 absolute -left-[17px] top-[178px] rounded-l-lg" data-id="element-21"></div>
          <div className="h-[64px] w-[3px] bg-gray-800 absolute -right-[17px] top-[142px] rounded-r-lg" data-id="element-22"></div>
          <div className="rounded-[2rem] overflow-hidden w-full h-full bg-black" data-id="element-23">
            {/* Apple Podcasts Home Feed UI */}
            <div className="w-full h-full bg-black flex flex-col" data-id="element-24">
              {/* Status Bar */}
              <div className="h-8 w-full flex justify-between items-center px-4 pt-1" data-id="element-25">
                <span className="text-white text-sm font-medium" data-id="element-26">2:12</span>
                <div className="flex items-center space-x-1" data-id="element-27">
                  <div className="h-4 w-4 rounded-full bg-gray-400 flex items-center justify-center overflow-hidden" data-id="element-28">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/mhPwKD1AKsyY4hHRVAFHnB/IMG_9126.png" alt="Profile" className="h-full w-full object-cover" data-id="element-29" />
                  </div>
                  <div className="flex items-center" data-id="element-30">
                    <span className="text-white text-xs" data-id="element-31">•••••</span>
                  </div>
                  <div className="flex items-center" data-id="element-32">
                    <span className="text-white text-xs" data-id="element-33">WiFi</span>
                  </div>
                  <div className="flex items-center" data-id="element-34">
                    <span className="text-white text-xs bg-gray-500 px-1 rounded" data-id="element-35">
                      99%
                    </span>
                  </div>
                </div>
              </div>
              {/* Home Header */}
              <div className="px-4 py-2" data-id="element-36">
                <div className="flex justify-between items-center" data-id="element-37">
                  <h1 className="text-white text-4xl font-bold" data-id="element-38">Home</h1>
                  <div className="h-8 w-8 rounded-full bg-purple-300 flex items-center justify-center" data-id="element-39">
                    <span className="text-purple-800 font-medium text-sm" data-id="element-40">
                      LR
                    </span>
                  </div>
                </div>
              </div>
              {/* Up Next Section */}
              <div className="px-4 py-2" data-id="element-41">
                <div className="flex justify-between items-center mb-2" data-id="element-42">
                  <h2 className="text-white text-2xl font-bold flex items-center" data-id="element-43">
                    Up Next{' '}
                    <ChevronRightIcon size={24} className="text-gray-400 ml-2" data-id="element-44" />
                  </h2>
                </div>
                {/* Horizontal scrollable container for episodes */}
                <div className="flex space-x-4 overflow-x-auto pb-4 no-scrollbar -mx-1 px-1" data-id="element-45">
                  {/* First Episode Card */}
                  <div className="bg-[#231f1c] rounded-xl overflow-hidden flex-shrink-0" style={{
                  width: '80%'
                }} data-id="element-46">
                    <div className="p-4" data-id="element-47">
                      <div className="flex flex-col" data-id="element-48">
                        <div className="w-full aspect-square rounded-lg overflow-hidden mb-3" data-id="element-49">
                          <img src={mainCoverImage} alt={mainVideo.title} className="w-full h-full object-cover" onError={e => {
                          e.currentTarget.src = 'https://via.placeholder.com/500x500?text=Thumbnail+Error';
                        }} data-id="element-50" />
                        </div>
                        <div className="text-gray-400 text-xs uppercase flex items-center mb-1" data-id="element-51">
                          RESUME · 17H AGO
                        </div>
                        <h3 className="text-white text-sm font-semibold mt-1 line-clamp-2" data-id="element-52">
                          {mainVideo.title || '177. Chelsea Handler: Why She Must Say It'}
                        </h3>
                        <p className="text-gray-400 text-sm mt-1 line-clamp-2" data-id="element-53">
                          {mainVideo.description || 'Chelsea Handler has achieved massive success, from her latest...'}
                        </p>
                      </div>
                      <div className="mt-4 flex items-center justify-between" data-id="element-54">
                        <div className="flex items-center bg-[#333] rounded-full pr-2" data-id="element-55">
                          <button className="bg-[#333] rounded-full p-2" data-id="element-56">
                            <PlayIcon size={16} className="text-white" data-id="element-57" />
                          </button>
                          <div className="ml-1 h-1 w-16 bg-gray-600 rounded-full" data-id="element-58">
                            <div className="h-1 w-1/3 bg-white rounded-full" data-id="element-59"></div>
                          </div>
                          <span className="text-white text-xs ml-2" data-id="element-60">26m</span>
                        </div>
                        <button className="p-1" data-id="element-61">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400" data-id="element-62">
                            <circle cx="12" cy="12" r="1" data-id="element-63"></circle>
                            <circle cx="19" cy="12" r="1" data-id="element-64"></circle>
                            <circle cx="5" cy="12" r="1" data-id="element-65"></circle>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* Second Episode Card - Partially Visible */}
                  <div className="bg-[#142d3b] rounded-xl overflow-hidden flex-shrink-0" style={{
                  width: '80%'
                }} data-id="element-66">
                    <div className="p-4" data-id="element-67">
                      <div className="flex flex-col" data-id="element-68">
                        <div className="w-full aspect-square rounded-lg overflow-hidden mb-3" data-id="element-69">
                          <img src={secondCoverImage} alt={secondVideo.title} className="w-full h-full object-cover" onError={e => {
                          e.currentTarget.src = 'https://via.placeholder.com/500x500?text=Thumbnail+Error';
                        }} data-id="element-70" />
                        </div>
                        <div className="text-gray-400 text-xs uppercase flex items-center mb-1" data-id="element-71">
                          RESUME · 13H AGO
                        </div>
                        <h3 className="text-white text-sm font-semibold mt-1 line-clamp-2" data-id="element-72">
                          {secondVideo.title || 'How Nature & Outdoor Environments Improve Focus, Cognition, & Mood'}
                        </h3>
                        <p className="text-gray-400 text-sm mt-1 line-clamp-2" data-id="element-73">
                          {secondVideo.description || 'Dr. Marc Berman discusses the cognitive benefits of nature...'}
                        </p>
                      </div>
                      <div className="mt-4 flex items-center justify-between" data-id="element-74">
                        <div className="flex items-center bg-[#333] rounded-full pr-2" data-id="element-75">
                          <button className="bg-[#333] rounded-full p-2" data-id="element-76">
                            <PlayIcon size={16} className="text-white" data-id="element-77" />
                          </button>
                          <div className="ml-1 h-1 w-16 bg-gray-600 rounded-full" data-id="element-78">
                            <div className="h-1 w-1/4 bg-white rounded-full" data-id="element-79"></div>
                          </div>
                          <span className="text-white text-xs ml-2" data-id="element-80">1h 4m</span>
                        </div>
                        <button className="p-1" data-id="element-81">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400" data-id="element-82">
                            <circle cx="12" cy="12" r="1" data-id="element-83"></circle>
                            <circle cx="19" cy="12" r="1" data-id="element-84"></circle>
                            <circle cx="5" cy="12" r="1" data-id="element-85"></circle>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* You Might Like Section */}
              <div className="px-4 py-3 mt-2" data-id="element-86">
                <div className="flex justify-between items-center mb-2" data-id="element-87">
                  <h2 className="text-white text-2xl font-bold" data-id="element-88">
                    You Might Like
                  </h2>
                  <ChevronRightIcon size={20} className="text-gray-400" data-id="element-89" />
                </div>
                <p className="text-gray-400 text-sm mb-3" data-id="element-90">
                  Based on your listening.
                </p>
                {/* Recommended Podcasts */}
                <div className="flex space-x-3 overflow-x-auto pb-3 no-scrollbar" data-id="element-91">
                  {recommendedVideos.map((video, index) => <div key={index} className="w-24 flex-shrink-0" data-id="element-92">
                      <div className="aspect-square rounded-lg overflow-hidden mb-1" data-id="element-93">
                        <img src={video.podcastCoverImage || video.customThumbnail} alt={video.title} className="w-full h-full object-cover" onError={e => {
                      e.currentTarget.src = 'https://via.placeholder.com/500x500?text=Thumbnail+Error';
                    }} data-id="element-94" />
                      </div>
                      <p className="text-white text-xs line-clamp-2" data-id="element-95">
                        {video.channelName}
                      </p>
                    </div>)}
                </div>
              </div>
              {/* Now Playing Bar */}
              <div className="mt-auto" data-id="element-96">
                <div className="bg-[#1c1c1c] p-2 rounded-t-xl border-t border-[#333]" data-id="element-97">
                  <div className="flex items-center" data-id="element-98">
                    <div className="h-10 w-10 rounded overflow-hidden mr-3" data-id="element-99">
                      <img src={mainCoverImage} alt={mainVideo.title} className="w-full h-full object-cover" data-id="element-100" />
                    </div>
                    <div className="flex-1" data-id="element-101">
                      <p className="text-white text-xs font-medium line-clamp-1" data-id="element-102">
                        How {mainVideo.channelName} can catch ChatGPT in
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-103">July 11</p>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-104">
                      <PlayIcon size={24} className="text-white" data-id="element-105" />
                      <div className="bg-white rounded-full h-6 w-6 flex items-center justify-center" data-id="element-106">
                        <span className="text-black text-xs font-medium" data-id="element-107">
                          30
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Bottom Navigation */}
                <div className="bg-[#1c1c1c] flex justify-around items-center py-3 px-2" data-id="element-108">
                  <div className="flex flex-col items-center" data-id="element-109">
                    <HomeIcon size={24} className="text-purple-500" data-id="element-110" />
                    <span className="text-purple-500 text-xs mt-1" data-id="element-111">Home</span>
                  </div>
                  <div className="flex flex-col items-center" data-id="element-112">
                    <LayoutGridIcon size={24} className="text-gray-400" data-id="element-113" />
                    <span className="text-gray-400 text-xs mt-1" data-id="element-114">Browse</span>
                  </div>
                  <div className="flex flex-col items-center" data-id="element-115">
                    <LibraryIcon size={24} className="text-gray-400" data-id="element-116" />
                    <span className="text-gray-400 text-xs mt-1" data-id="element-117">Library</span>
                  </div>
                  <div className="flex flex-col items-center" data-id="element-118">
                    <SearchIcon size={24} className="text-gray-400" data-id="element-119" />
                    <span className="text-gray-400 text-xs mt-1" data-id="element-120">Search</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};